import React from "react";
import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import {
  LazySingleSelector,
  LazySingleSelectorProps,
} from "../selector/LazySingleSelector";
import { FormFieldControl } from "./FormFieldControl";

export type LazySingleSelectorFieldProps<
  TFormValues extends FieldValues,
  TValue extends string,
> = Omit<
  LazySingleSelectorProps<TValue>,
  | "value"
  | "onChange"
  | "name"
  | "form"
  | "isInvalid"
  | "isRequired"
  | "isDisabled"
> & {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  rules?: RegisterOptions<TFormValues, FieldPath<TFormValues>>;
  label?: string;
  helperText?: React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
};

export function LazySingleSelectorField<
  TFormValues extends FieldValues,
  TValue extends string,
>({
  form,
  name,
  rules,
  label,
  helperText,
  placeholder,
  isRequired,
  isDisabled,
  options,
  selectedOption,
  enableDeselect,
  ...selectorProps
}: LazySingleSelectorFieldProps<TFormValues, TValue>) {
  const requiredRule =
    rules?.required ??
    (isRequired ? `${label ?? "This field"} is required` : false);

  return (
    <FormFieldControl
      form={form}
      name={name}
      label={label}
      helperText={helperText}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Controller
        control={form.control}
        name={name}
        rules={{
          ...rules,
          required: requiredRule,
        }}
        render={({ field, fieldState }) => (
          <LazySingleSelector
            {...selectorProps}
            options={options}
            selectedOption={selectedOption}
            enableDeselect={enableDeselect}
            value={field.value || undefined}
            onChange={(value: TValue | undefined) =>
              field.onChange(value ?? "")
            }
            isRequired={isRequired}
            isDisabled={isDisabled || form.formState.isSubmitting}
            isInvalid={!!fieldState.error && !form.formState.isSubmitting}
            placeholder={placeholder ?? label}
          />
        )}
      />
    </FormFieldControl>
  );
}
